import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Video, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, Plus, Search, Loader2, X, MessageSquare,
  ClipboardList, Check, Phone, Settings, Trash2, Mail, CalendarDays, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import {
  listAppointments, getAppointment, startVisit, completeVisit, updateAppointment, deleteAppointment,
  statusMeta, intakeStateOf, isEnded, isStarted, getChartSnapshot,
} from "@/lib/telehealth";
import { LiveVisit, Thread, inviteUrl, fmtTime } from "@/components/chart/TelehealthVisits";
import { IntakeView } from "@/components/telehealth/IntakeView";
import { LiveSignals } from "@/components/telehealth/LiveSignals";
import { SlotGrid } from "@/components/telehealth/SlotGrid";
import ActivityTracker from "@/components/telehealth/ActivityTracker";
import { getProviderConfig } from "@/lib/scheduler";
import ScheduleModal from "@/components/patients/ScheduleModal";

const DAY_MS = 86400000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const sameDay = (a, b) => ymd(a) === ymd(b);
const fmtDayLabel = (d) => new Date(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const fmtHM = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); };
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function SummaryCard({ label, value, tone, testid }) {
  const c = { today: "#4B4BE8", upcoming: "#166534", waiting: "#9A5A00", done: "#44443F", noshow: "#B42318" }[tone] || "#141412";
  return (
    <div className="rounded-[14px] px-4 py-3.5 flex-1 min-w-[140px]" style={{ background: "#fff", border: "1px solid #ECECEF" }} data-testid={testid}>
      <div className="text-[26px] font-bold leading-none tabular-nums" style={{ color: c }}>{value}</div>
      <div className="text-[11.5px] font-semibold mt-1.5" style={{ color: "#6C6C69" }}>{label}</div>
    </div>
  );
}

// Compact board row — the full set of visit actions.
function BoardRow({ a, acting, on }) {
  const sm = statusMeta(a.status);
  const ended = isEnded(a.status);
  const started = isStarted(a.status);
  const intake = intakeStateOf(a);
  const chartUuid = a.patient?.chart_uuid;
  const busy = acting === a.appointment_id;
  return (
    <div className="rounded-[12px] p-3" style={{ background: "#fff", border: "1px solid #ECECEF" }} data-testid={`tm-row-${a.appointment_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center justify-center flex-none w-[62px] text-center">
          <div className="text-[14px] font-bold tabular-nums" style={{ color: "#141412" }}>{fmtHM(a.scheduled_at)}</div>
          <div className="text-[10.5px]" style={{ color: "#9B9B97" }}>{a.duration_minutes || 30}m</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-bold" style={{ color: "#141412" }}>{a.patient?.name || "Patient"}</span>
            <span className="text-[11px] px-2 py-0.5 rounded-full font-bold" style={{ background: sm.bg, color: sm.fg }} data-testid={`tm-status-${a.appointment_id}`}>{sm.t}</span>
            {a.patient_waiting ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FFF4E0", color: "#9A5A00" }}><span className="w-[6px] h-[6px] rounded-full" style={{ background: "#F08C00" }} />Waiting</span> : null}
            {a.unread_count > 0 ? <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: "#C2410C" }}>{a.unread_count} new</span> : null}
            {intake ? <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-[5px]" style={{ background: intake === "done" ? "#E7F7EC" : "#FFF4E0", color: intake === "done" ? "#166534" : "#9A5A00" }}>{intake === "done" ? "Intake done" : "Intake pending"}</span> : null}
          </div>
          <div className="text-[12px] mt-1" style={{ color: "#6C6C69" }}>{a.visit_type_name ? `${a.visit_type_name} · ` : ""}{a.reason || "Telehealth visit"} · {a.provider_name}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
        {!ended && !started ? <button onClick={() => on.start(a)} disabled={busy} data-testid={`tm-start-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-bold px-3 py-1.5 rounded-[8px] text-white" style={{ background: "#1F8A4C" }}>{busy ? <Loader2 size={12} className="animate-spin" /> : <Video size={13} />} Start</button> : null}
        {!ended && started ? <button onClick={() => on.start(a)} disabled={busy} data-testid={`tm-rejoin-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-bold px-3 py-1.5 rounded-[8px] text-white" style={{ background: "#1E40AF" }}>{busy ? <Loader2 size={12} className="animate-spin" /> : <Video size={13} />} Rejoin</button> : null}
        {!ended && started ? <button onClick={() => on.complete(a)} disabled={busy} data-testid={`tm-complete-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-bold px-3 py-1.5 rounded-[8px]" style={{ border: "1px solid #1F8A4C", color: "#166534" }}><Check size={13} /> Complete</button> : null}
        <button onClick={() => on.message(a)} data-testid={`tm-message-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#141412" }}><MessageSquare size={13} /> Message{a.unread_count > 0 ? ` (${a.unread_count})` : ""}</button>
        <button onClick={() => on.intake(a)} data-testid={`tm-details-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#141412" }}><ClipboardList size={13} /> Details</button>
        {(a.patient_link_path || a.room_url) ? <button onClick={() => on.invite(a)} data-testid={`tm-invite-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#141412" }}><Mail size={13} /> Invite</button> : null}
        {chartUuid ? <button onClick={() => on.openChart(a)} data-testid={`tm-chart-${a.appointment_id}`} className="inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#141412" }}><ExternalLink size={13} /> Chart</button> : null}
        {!ended && a.status !== "confirmed" ? <button onClick={() => on.confirm(a)} disabled={busy} data-testid={`tm-confirm-${a.appointment_id}`} className="text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#166534" }}>Confirm</button> : null}
        {!ended ? <button onClick={() => on.reschedule(a)} disabled={busy} data-testid={`tm-resched-${a.appointment_id}`} className="text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#141412" }}>Reschedule</button> : null}
        {!ended ? <button onClick={() => on.cancel(a)} disabled={busy} data-testid={`tm-cancel-${a.appointment_id}`} className="text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#B42318" }}>Cancel</button> : null}
        {!ended ? <button onClick={() => on.noshow(a)} disabled={busy} data-testid={`tm-noshow-${a.appointment_id}`} className="text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#9A5A00" }}>No-show</button> : null}
        <button onClick={() => on.del(a)} disabled={busy} data-testid={`tm-delete-${a.appointment_id}`} aria-label="Delete" className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center" style={{ border: "1px solid #E4E4E7", color: "#9B9B97" }}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

function PatientPicker({ clientId, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setRows([]); return undefined; }
    let alive = true; setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: clientId, q: q.trim() } }); if (alive) setRows(Array.isArray(data?.results) ? data.results : []); }
      catch { if (alive) setRows([]); } finally { if (alive) setLoading(false); }
    }, 320);
    return () => { alive = false; clearTimeout(t); };
  }, [q, clientId]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 4600, background: "rgba(20,20,18,.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "10vh" }} onMouseDown={onClose}>
      <div onMouseDown={(e) => e.stopPropagation()} className="bg-white rounded-[14px] w-[460px] max-w-[94vw] overflow-hidden" style={{ boxShadow: "0 24px 70px rgba(20,20,18,.4)" }} data-testid="tm-patient-picker">
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #ECECEF" }}>
          <Search size={16} style={{ color: "#9B9B97" }} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a patient to book…" data-testid="tm-patient-search-input" className="flex-1 text-[14px] outline-none" />
          {loading ? <Loader2 size={15} className="animate-spin" style={{ color: "#9B9B97" }} /> : null}
          <button onClick={onClose} className="w-[28px] h-[28px] rounded-[7px] flex items-center justify-center hover:bg-black/[.05]" style={{ color: "#6C6C69" }}><X size={15} /></button>
        </div>
        <div className="max-h-[52vh] overflow-y-auto">
          {q.trim() && !loading && rows.length === 0 ? <div className="py-8 text-center text-[12.5px]" style={{ color: "#9B9B97" }}>No patients found.</div> : null}
          {!q.trim() ? <div className="py-8 text-center text-[12.5px]" style={{ color: "#9B9B97" }}>Type a name, MRN or payer to find a patient.</div> : null}
          {rows.map((p, i) => {
            const name = p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Patient";
            return (
              <button key={p.chart_uuid || p.mrn || i} onClick={() => onPick(p)} data-testid={`tm-patient-pick-${i}`} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-black/[.03]" style={{ borderTop: i ? "1px solid #F1F1F4" : "none" }}>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold truncate" style={{ color: "#141412" }}>{name}</div>
                  <div className="text-[11.5px]" style={{ color: "#9B9B97" }}>MRN {p.mrn || "—"}{p.dob ? ` · ${p.dob}` : ""}</div>
                </div>
                <Plus size={15} style={{ color: "#4B4BE8" }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Telemedicine() {
  const { selectedClientId } = useClient();
  const navigate = useNavigate();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | day | week | month
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [provider, setProvider] = useState("all");
  const [acting, setActing] = useState(null);
  const [live, setLive] = useState(null);
  const [liveSnap, setLiveSnap] = useState({});
  const [threadId, setThreadId] = useState(null);
  const [intakeId, setIntakeId] = useState(null);
  const [resched, setResched] = useState(null); // {id, npi, providerName, date, time, slot}
  const [reschedConfig, setReschedConfig] = useState(null);
  const [picker, setPicker] = useState(false);
  const [bookPatient, setBookPatient] = useState(null);
  const [mode, setMode] = useState("schedule"); // schedule | activity
  const [reviewing, setReviewing] = useState(false); // a visit note is open in Activity — pause auto-reload

  const load = useCallback(async (silent) => {
    if (!selectedClientId) { setAppts([]); setLoading(false); return; }
    if (!silent) setLoading(true);
    try { setAppts(await listAppointments(selectedClientId)); }
    catch { if (!silent) toast.error("Couldn't load visits"); }
    finally { if (!silent) setLoading(false); }
  }, [selectedClientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(() => { if (!document.hidden && !live && !reviewing) load(true); }, 15000); return () => clearInterval(iv); }, [load, live, reviewing]);

  // Load the provider's availability config when a reschedule opens (for the slot picker).
  useEffect(() => {
    if (!resched?.npi || !selectedClientId) { return undefined; }
    let alive = true;
    getProviderConfig(selectedClientId, resched.npi).then((c) => { if (alive) setReschedConfig(c); }).catch(() => { if (alive) setReschedConfig(null); });
    return () => { alive = false; };
  }, [resched?.npi, selectedClientId]);

  const providers = useMemo(() => Array.from(new Set(appts.map((a) => a.provider_name).filter(Boolean))).sort(), [appts]);
  const scoped = useMemo(() => appts.filter((a) => provider === "all" || a.provider_name === provider), [appts, provider]);
  const activityAppts = useMemo(() => scoped.filter((a) => a.visit_id && (a.status === "completed" || isEnded(a.status))), [scoped]);

  // Summary cards — global for today, from the provider-scoped set.
  const summary = useMemo(() => {
    const today = new Date();
    const s = { today: 0, upcoming: 0, waiting: 0, done: 0, noshow: 0 };
    for (const a of scoped) {
      const d = new Date(a.scheduled_at);
      const isToday = !isNaN(d) && sameDay(d, today);
      if (isToday) s.today += 1;
      if (!isEnded(a.status) && !isNaN(d) && d >= today) s.upcoming += 1;
      if (a.status === "waiting_room" || a.status === "waiting" || a.patient_waiting) s.waiting += 1;
      if (isToday && a.status === "completed") s.done += 1;
      if (isToday && a.status === "no_show") s.noshow += 1;
    }
    return s;
  }, [scoped]);

  const patch = (id, p) => setAppts((all) => all.map((a) => a.appointment_id === id ? { ...a, ...p } : a));
  const act = async (id, fn, okMsg) => { setActing(id); try { const u = await fn(); if (u && u.appointment_id) patch(id, u); else await load(true); if (okMsg) toast.success(okMsg); } catch { toast.error("Action failed"); } finally { setActing(null); } };

  const on = {
    start: async (a) => {
      setActing(a.appointment_id);
      setLiveSnap({});
      try {
        const s = await startVisit(a.appointment_id);
        let full = a; try { full = await getAppointment(a.appointment_id); } catch { /* fall back */ }
        setLive({ ...full, room_url: s.room_url || full.room_url, join_token: s.token || null, display_name: s.display_name || full.provider_name });
        load(true);
        const chartUuid = full.patient?.chart_uuid || a.patient?.chart_uuid;
        if (chartUuid) getChartSnapshot(chartUuid).then((snap) => { if (snap) setLiveSnap(snap); }).catch(() => {});
      } catch { toast.error("Couldn't start the visit"); } finally { setActing(null); }
    },
    complete: (a) => act(a.appointment_id, () => completeVisit(a.appointment_id), "Visit completed — the note is written from the recording"),
    confirm: (a) => act(a.appointment_id, () => updateAppointment(a.appointment_id, { status: "confirmed" }), "Confirmed"),
    cancel: (a) => act(a.appointment_id, () => updateAppointment(a.appointment_id, { status: "cancelled" }), "Cancelled"),
    noshow: (a) => act(a.appointment_id, () => updateAppointment(a.appointment_id, { status: "no_show" }), "Marked no-show"),
    message: (a) => setThreadId(a.appointment_id),
    intake: (a) => setIntakeId(a.appointment_id),
    invite: (a) => { navigator.clipboard?.writeText(inviteUrl(a)); toast.success("Patient link copied"); },
    reschedule: (a) => { const d = new Date(a.scheduled_at); setReschedConfig(null); setResched({ id: a.appointment_id, npi: a.npi || a.provider_npi || "", providerName: a.provider_name, date: isNaN(d) ? "" : ymd(d), time: isNaN(d) ? "" : d.toTimeString().slice(0, 5), slot: null }); },
    del: async (a) => { setAppts((all) => all.filter((x) => x.appointment_id !== a.appointment_id)); try { await deleteAppointment(a.appointment_id); toast.success("Appointment deleted"); } catch { toast.error("Couldn't delete"); load(true); } },
    openChart: (a) => { const u = a.patient?.chart_uuid; if (u) window.open(`/chart-v2?uuid=${encodeURIComponent(u)}&tab=engagement`, "_blank"); },
  };

  const doResched = async () => {
    const { id, date, time, slot } = resched;
    const reschedConfigured = !!reschedConfig && Object.values(reschedConfig.weekly || {}).some((w) => (w || []).length);
    let iso;
    if (reschedConfigured) { if (!slot) { toast.error("Pick a time slot"); return; } iso = new Date(slot).toISOString(); }
    else { if (!date || !time) return; iso = new Date(`${date}T${time}:00`).toISOString(); }
    setResched(null); setReschedConfig(null);
    await act(id, () => updateAppointment(id, { scheduled_at: iso }), "Rescheduled");
  };

  const threadAppt = appts.find((a) => a.appointment_id === threadId);
  const intakeAppt = appts.find((a) => a.appointment_id === intakeId);

  // Date range label + navigation step by view.
  const rangeLabel = useMemo(() => {
    if (view === "list") return "All dates";
    if (view === "day") return new Date(anchor).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
    if (view === "week") { const s = startOfDay(anchor); s.setDate(s.getDate() - s.getDay()); const e = new Date(s.getTime() + 6 * DAY_MS); return `${s.toLocaleDateString([], { month: "short", day: "numeric" })} – ${e.toLocaleDateString([], { month: "short", day: "numeric" })}`; }
    return new Date(anchor).toLocaleDateString([], { month: "long", year: "numeric" });
  }, [view, anchor]);

  const stepDate = (dir) => setAnchor((cur) => { const d = new Date(cur); if (view === "day") d.setDate(d.getDate() + dir); else if (view === "week") d.setDate(d.getDate() + 7 * dir); else if (view === "month") d.setMonth(d.getMonth() + dir); return startOfDay(d); });

  // Group visits by local date for list/day rendering.
  const byDate = useMemo(() => {
    const map = new Map();
    for (const a of scoped) { const k = ymd(a.scheduled_at); if (!map.has(k)) map.set(k, []); map.get(k).push(a); }
    for (const v of map.values()) v.sort((x, y) => new Date(x.scheduled_at) - new Date(y.scheduled_at));
    return map;
  }, [scoped]);

  const listGroups = useMemo(() => {
    let keys = Array.from(byDate.keys());
    if (view === "day") keys = keys.filter((k) => k === ymd(anchor));
    keys.sort((x, y) => (x < y ? 1 : x > y ? -1 : 0)); // newest date first
    return keys.map((k) => [k, byDate.get(k)]);
  }, [byDate, view, anchor]);

  // Date groups are collapsed by default; today's group opens automatically.
  const todayKey = ymd(new Date());
  const [openDates, setOpenDates] = useState({});
  const isDateOpen = (k) => (k in openDates ? openDates[k] : k === todayKey);
  const toggleDate = (k) => setOpenDates((m) => ({ ...m, [k]: !(k in m ? m[k] : k === todayKey) }));
  const groupCounts = (rows) => {
    const c = { open: 0, done: 0, cancelled: 0 };
    for (const a of rows) {
      if (a.status === "completed") c.done += 1;
      else if (a.status === "cancelled" || a.status === "canceled" || a.status === "no_show") c.cancelled += 1;
      else c.open += 1;
    }
    return c;
  };

  return (
    <div className="px-6 py-5 max-w-[1320px] mx-auto" data-testid="telemedicine-console">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Video size={22} style={{ color: "#4B4BE8" }} />
        <div className="min-w-0">
          <h1 className="text-[20px] font-bold tracking-[-.01em]" style={{ color: "#141412" }}>Telemedicine</h1>
          <div className="text-[12px]" style={{ color: "#8A8A86" }}>Every telehealth visit for Client {selectedClientId || "—"} · updates live</div>
        </div>
        <div className="flex gap-[3px] p-[3px] rounded-[10px] ml-2" style={{ background: "#F1F1F0" }} data-testid="tm-mode-switch">
          {[["schedule", "Scheduling"], ["activity", "Activity"]].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`tm-mode-${m}`} className="px-3.5 py-1.5 rounded-[7px] text-[12px] font-semibold transition-all" style={{ background: mode === m ? "#fff" : "transparent", color: mode === m ? "#141412" : "#6C6C69", boxShadow: mode === m ? "0 1px 3px rgba(0,0,0,.12)" : "none" }}>{label}</button>
          ))}
        </div>
        <span className="flex-1" />
        <button onClick={() => load()} data-testid="tm-refresh" className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center" style={{ border: "1px solid #E4E4E7", color: "#6C6C69" }}><RefreshCw size={15} /></button>
        <button onClick={() => navigate(`/settings?step=availability&client=${encodeURIComponent(selectedClientId)}${(() => { if (provider === "all") return ""; const a = appts.find((x) => x.provider_name === provider && (x.npi || x.provider_npi)); return a ? `&npi=${encodeURIComponent(a.npi || a.provider_npi)}` : ""; })()}`)} title="Availability settings" data-testid="tm-settings" className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center" style={{ border: "1px solid #E4E4E7", color: "#6C6C69" }}><Settings size={15} /></button>
        <button onClick={() => setPicker(true)} data-testid="tm-new-visit" className="inline-flex items-center gap-1.5 text-[13px] font-bold px-4 py-2 rounded-[9px] text-white" style={{ background: "#141412" }}><Plus size={15} /> New visit</button>
      </div>

      {/* Summary cards */}
      {mode === "schedule" ? (
      <div className="flex gap-3 flex-wrap mb-4">
        <SummaryCard label="Today" value={summary.today} tone="today" testid="tm-sum-today" />
        <SummaryCard label="Upcoming" value={summary.upcoming} tone="upcoming" testid="tm-sum-upcoming" />
        <SummaryCard label="Waiting room" value={summary.waiting} tone="waiting" testid="tm-sum-waiting" />
        <SummaryCard label="Completed today" value={summary.done} tone="done" testid="tm-sum-done" />
        <SummaryCard label="No-shows today" value={summary.noshow} tone="noshow" testid="tm-sum-noshow" />
      </div>
      ) : null}

      {/* Toolbar: view switch + provider + date nav */}
      {mode === "schedule" ? (
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex gap-[3px] p-[3px] rounded-[10px]" style={{ background: "#F1F1F0" }}>
          {["list", "day", "week", "month"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`tm-view-${v}`} className="px-3 py-1.5 rounded-[7px] text-[12px] font-semibold capitalize transition-all" style={{ background: view === v ? "#fff" : "transparent", color: view === v ? "#141412" : "#6C6C69", boxShadow: view === v ? "0 1px 3px rgba(0,0,0,.12)" : "none" }}>{v}</button>
          ))}
        </div>
        <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="tm-provider-filter" className="h-[34px] px-3 rounded-[9px] text-[12.5px] outline-none" style={{ border: "1px solid #E4E4E7", color: "#141412", background: "#fff" }}>
          <option value="all">All providers</option>
          {providers.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="flex-1" />
        {view !== "list" ? (
          <div className="flex items-center gap-1.5">
            <button onClick={() => stepDate(-1)} data-testid="tm-date-prev" className="w-[32px] h-[32px] rounded-[8px] flex items-center justify-center" style={{ border: "1px solid #E4E4E7", color: "#6C6C69" }}><ChevronLeft size={16} /></button>
            <button onClick={() => setAnchor(startOfDay(new Date()))} data-testid="tm-date-today" className="text-[12px] font-semibold px-3 h-[32px] rounded-[8px]" style={{ border: "1px solid #E4E4E7", color: "#141412" }}>Today</button>
            <button onClick={() => stepDate(1)} data-testid="tm-date-next" className="w-[32px] h-[32px] rounded-[8px] flex items-center justify-center" style={{ border: "1px solid #E4E4E7", color: "#6C6C69" }}><ChevronRight size={16} /></button>
          </div>
        ) : null}
        <span className="text-[13px] font-semibold min-w-[120px] text-right" style={{ color: "#141412" }} data-testid="tm-range-label">{rangeLabel}</span>
      </div>
      ) : null}

      {/* Body */}
      {mode === "activity" ? (
        <ActivityTracker appointments={activityAppts} clientId={selectedClientId} onReviewing={setReviewing} onOpenClaim={(noteId) => navigate(noteId ? `/create-claims?claim=${encodeURIComponent(noteId)}&client=${encodeURIComponent(selectedClientId)}` : "/create-claims")} />
      ) : loading ? (
        <div className="py-16 text-center text-[13px]" style={{ color: "#9B9B97" }}><Loader2 size={18} className="animate-spin mx-auto mb-2" /> Loading visits…</div>
      ) : scoped.length === 0 ? (
        <div className="py-16 text-center" data-testid="tm-empty">
          <CalendarDays size={28} className="mx-auto mb-3" style={{ color: "#CFCFCB" }} />
          <div className="text-[14px] font-semibold" style={{ color: "#141412" }}>No telehealth visits yet</div>
          <div className="text-[12.5px] mt-1" style={{ color: "#9B9B97" }}>Book one with the “New visit” button.</div>
        </div>
      ) : view === "week" ? (
        <WeekGrid anchor={anchor} byDate={byDate} onDay={(d) => { setAnchor(startOfDay(d)); setView("day"); }} />
      ) : view === "month" ? (
        <MonthGrid anchor={anchor} byDate={byDate} onDay={(d) => { setAnchor(startOfDay(d)); setView("day"); }} />
      ) : (
        <div className="space-y-3" data-testid="tm-list">
          {listGroups.length === 0 ? <div className="py-12 text-center text-[13px]" style={{ color: "#9B9B97" }}>No visits for this day.</div>
            : listGroups.map(([k, rows]) => {
              const open = isDateOpen(k);
              const c = groupCounts(rows);
              return (
                <div key={k} className="rounded-[12px] overflow-hidden" style={{ border: "1px solid #ECECEF", background: "#fff" }}>
                  <button onClick={() => toggleDate(k)} data-testid={`tm-dategroup-${k}`} className="w-full flex items-center gap-2.5 px-3.5 py-3 text-left hover:bg-black/[.015] transition-colors">
                    <ChevronDown size={16} style={{ color: "#9B9B97", transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s" }} />
                    <span className="text-[13px] font-bold" style={{ color: "#141412" }}>{fmtDayLabel(k + "T00:00:00")}</span>
                    {k === todayKey ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#EEF0FF", color: "#3730A3" }}>Today</span> : null}
                    <span className="text-[11.5px]" style={{ color: "#9B9B97" }}>· {rows.length} visit{rows.length === 1 ? "" : "s"}</span>
                    <span className="flex-1" />
                    <span className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                      {c.open > 0 ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#EEF0FF", color: "#3730A3" }} data-testid={`tm-grp-open-${k}`}>{c.open} open</span> : null}
                      {c.done > 0 ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#E7F7EC", color: "#166534" }} data-testid={`tm-grp-done-${k}`}>{c.done} completed</span> : null}
                      {c.cancelled > 0 ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FCE9E7", color: "#B42318" }} data-testid={`tm-grp-cancelled-${k}`}>{c.cancelled} cancelled</span> : null}
                    </span>
                  </button>
                  {open ? <div className="space-y-2 px-2.5 pb-2.5 pt-0.5">{rows.map((a) => <BoardRow key={a.appointment_id} a={a} acting={acting} on={on} />)}</div> : null}
                </div>
              );
            })}
        </div>
      )}

      {/* Drawers & overlays */}
      {threadAppt ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 4400, background: "rgba(20,20,18,.32)", display: "flex", justifyContent: "flex-end" }} onMouseDown={() => setThreadId(null)}>
          <aside onMouseDown={(e) => e.stopPropagation()} style={{ width: "min(420px,96vw)", height: "100%", background: "#fff", display: "flex", flexDirection: "column" }} data-testid="tm-thread">
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #ECECEF" }}>
              <div className="min-w-0 flex-1"><div className="text-[13.5px] font-bold" style={{ color: "#141412" }}>Messages</div><div className="text-[11.5px] truncate" style={{ color: "#8A8A86" }}>{threadAppt.patient?.name} · {fmtTime(threadAppt.scheduled_at)}</div></div>
              <button onClick={() => setThreadId(null)} data-testid="tm-thread-close" className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center hover:bg-black/[.05]" style={{ color: "#6C6C69" }}><X size={16} /></button>
            </div>
            <div className="flex-1 min-h-0"><Thread apptId={threadId} intervalMs={5000} onUnreadClear={(id) => patch(id, { unread_count: 0 })} /></div>
          </aside>
        </div>
      ) : null}

      {intakeAppt ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 4400, background: "rgba(20,20,18,.32)", display: "flex", justifyContent: "flex-end" }} onMouseDown={() => setIntakeId(null)}>
          <aside onMouseDown={(e) => e.stopPropagation()} style={{ width: "min(460px,96vw)", height: "100%", background: "#FBFBFA", display: "flex", flexDirection: "column" }} data-testid="tm-intake-drawer">
            <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #ECECEF", background: "#fff" }}>
              <ClipboardList size={16} style={{ color: "#4B4BE8" }} />
              <div className="min-w-0 flex-1"><div className="text-[13.5px] font-bold" style={{ color: "#141412" }}>Appointment details</div><div className="text-[11.5px] truncate" style={{ color: "#8A8A86" }}>{intakeAppt.patient?.name} · {fmtTime(intakeAppt.scheduled_at)}</div></div>
              <button onClick={() => setIntakeId(null)} data-testid="tm-intake-close" className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center hover:bg-black/[.05]" style={{ color: "#6C6C69" }}><X size={16} /></button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <LiveSignals appt={intakeAppt} />
              <div className="h-[1px] mx-3" style={{ background: "#ECECEF" }} />
              <IntakeView token={intakeAppt.patient_link_token} />
            </div>
          </aside>
        </div>
      ) : null}

      {resched ? (() => {
        const reschedConfigured = !!reschedConfig && Object.values(reschedConfig.weekly || {}).some((w) => (w || []).length);
        return (
        <div style={{ position: "fixed", inset: 0, zIndex: 4500, background: "rgba(20,20,18,.4)", display: "flex", alignItems: "center", justifyContent: "center" }} onMouseDown={() => { setResched(null); setReschedConfig(null); }}>
          <div onMouseDown={(e) => e.stopPropagation()} data-testid="tm-resched" className="bg-white rounded-[14px] p-4 w-[360px] max-w-[94vw]" style={{ boxShadow: "0 24px 70px rgba(20,20,18,.4)" }}>
            <div className="text-[14px] font-bold mb-3" style={{ color: "#141412" }}>Reschedule visit</div>
            {reschedConfigured ? (
              <SlotGrid config={reschedConfig} date={resched.date}
                onDateChange={(d) => setResched((r) => ({ ...r, date: d, slot: null }))}
                appointments={appts.filter((a) => a.appointment_id !== resched.id)}
                npi={resched.npi} providerName={resched.providerName}
                selectedStart={resched.slot} onPick={(d) => setResched((r) => ({ ...r, slot: d }))} />
            ) : (
              <>
                <label className="text-[11.5px] font-semibold" style={{ color: "#6C6C69" }}>Date</label>
                <input type="date" value={resched.date} onChange={(e) => setResched((r) => ({ ...r, date: e.target.value }))} data-testid="tm-resched-date" className="w-full h-[36px] px-3 rounded-[9px] text-[13px] outline-none mt-1 mb-2.5" style={{ border: "1px solid #E4E4E7" }} />
                <label className="text-[11.5px] font-semibold" style={{ color: "#6C6C69" }}>Time</label>
                <input type="time" step="900" value={resched.time} onChange={(e) => setResched((r) => ({ ...r, time: e.target.value }))} data-testid="tm-resched-time" className="w-full h-[36px] px-3 rounded-[9px] text-[13px] outline-none mt-1 mb-3" style={{ border: "1px solid #E4E4E7" }} />
              </>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setResched(null); setReschedConfig(null); }} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-[8px]" style={{ color: "#6C6C69" }}>Cancel</button>
              <button onClick={doResched} data-testid="tm-resched-save" className="text-[12.5px] font-bold px-3.5 py-1.5 rounded-[8px] text-white" style={{ background: "#141412" }}>Save</button>
            </div>
          </div>
        </div>
        );
      })() : null}


      {picker ? <PatientPicker clientId={selectedClientId} onClose={() => setPicker(false)} onPick={(p) => { setPicker(false); setBookPatient(p); }} /> : null}
      <ScheduleModal open={!!bookPatient} onOpenChange={(o) => { if (!o) { setBookPatient(null); load(true); } }} patient={bookPatient} clientId={selectedClientId} />

      {live ? <LiveVisit appt={live} snapshot={liveSnap} onClose={() => { setLive(null); setLiveSnap({}); }} onCompleted={() => load(true)} /> : null}
    </div>
  );
}

// Read-only week overview — a column per day with visit chips. Clicking a day opens it.
function WeekGrid({ anchor, byDate, onDay }) {
  const start = startOfDay(anchor); start.setDate(start.getDate() - start.getDay());
  const days = Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * DAY_MS));
  const today = new Date();
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="tm-week">
      {days.map((d) => {
        const rows = byDate.get(ymd(d)) || [];
        const isToday = sameDay(d, today);
        return (
          <button key={ymd(d)} onClick={() => onDay(d)} data-testid={`tm-week-day-${ymd(d)}`} className="text-left rounded-[12px] p-2.5 min-h-[160px] align-top transition-colors hover:bg-black/[.02]" style={{ background: "#fff", border: `1px solid ${isToday ? "#4B4BE8" : "#ECECEF"}` }}>
            <div className="text-[11px] font-bold mb-2" style={{ color: isToday ? "#4B4BE8" : "#6C6C69" }}>{WEEKDAYS[d.getDay()]} {d.getDate()}</div>
            <div className="space-y-1">
              {rows.slice(0, 6).map((a) => { const sm = statusMeta(a.status); return (
                <div key={a.appointment_id} className="rounded-[6px] px-1.5 py-1 text-[10.5px] truncate" style={{ background: sm.bg, color: sm.fg }}>{fmtHM(a.scheduled_at)} {a.patient?.name || "Visit"}</div>
              ); })}
              {rows.length > 6 ? <div className="text-[10.5px] font-semibold" style={{ color: "#9B9B97" }}>+{rows.length - 6} more</div> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Read-only month overview — a 6×7 grid of day cells with visit counts.
function MonthGrid({ anchor, byDate, onDay }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = new Date(first.getTime() - first.getDay() * DAY_MS);
  const cells = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getTime() + i * DAY_MS));
  const today = new Date();
  return (
    <div data-testid="tm-month">
      <div className="grid grid-cols-7 gap-2 mb-1.5">{WEEKDAYS.map((w) => <div key={w} className="text-[10.5px] font-bold uppercase tracking-[.04em] text-center" style={{ color: "#9B9B97" }}>{w}</div>)}</div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((d) => {
          const rows = byDate.get(ymd(d)) || [];
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          return (
            <button key={ymd(d)} onClick={() => onDay(d)} data-testid={`tm-month-day-${ymd(d)}`} className="text-left rounded-[10px] p-2 min-h-[92px] align-top transition-colors hover:bg-black/[.02]" style={{ background: inMonth ? "#fff" : "#FAFAF9", border: `1px solid ${isToday ? "#4B4BE8" : "#ECECEF"}`, opacity: inMonth ? 1 : 0.6 }}>
              <div className="text-[11.5px] font-bold mb-1" style={{ color: isToday ? "#4B4BE8" : "#6C6C69" }}>{d.getDate()}</div>
              {rows.slice(0, 2).map((a) => { const sm = statusMeta(a.status); return (
                <div key={a.appointment_id} className="rounded-[5px] px-1 py-0.5 text-[9.5px] truncate mb-0.5" style={{ background: sm.bg, color: sm.fg }}>{fmtHM(a.scheduled_at)} {a.patient?.name || "Visit"}</div>
              ); })}
              {rows.length > 2 ? <div className="text-[9.5px] font-semibold" style={{ color: "#9B9B97" }}>+{rows.length - 2}</div> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
